import { useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { LuCopy, LuMessageSquare, LuRefreshCw } from "react-icons/lu";
import {
  Box,
  Button,
  Card,
  Code,
  Dialog,
  Field,
  Heading,
  HStack,
  Input,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";

type EventDoc = {
  _id: Id<"events">;
  hasFeedbackForm: boolean;
  feedbackToken: string | null;
  feedbackClosesOn: string | null;
};

/**
 * Anonymous feedback, from the admin's side.
 *
 * Counts, the answers, and the rate against the roster. Never who -- there is
 * no person reference on the row to show, which is the design rather than a
 * restriction applied here (model §14).
 *
 * What that costs is worth keeping in mind while reading the number: nobody
 * can be chased for a response, and "who has not answered" cannot be asked.
 */
export function EventFeedbackPanel({ event }: { event: EventDoc }) {
  const responses = useQuery(
    api.eventFeedback.listResponses,
    event.hasFeedbackForm ? { eventId: event._id } : "skip",
  );
  const [editing, setEditing] = useState(false);

  const link = event.feedbackToken === null
    ? null
    : `${window.location.origin}/feedback/${event.feedbackToken}`;

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={5}>
        <Stack gap={4}>
          <HStack justify="space-between" align="end" wrap="wrap" gap={3}>
            <Box>
              <Heading size="md">Feedback</Heading>
              <Text mt={1} fontSize="sm" color="fg.muted">
                Anonymous: anyone with the link can answer without signing in, and nobody can see
                who answered.
              </Text>
            </Box>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <LuMessageSquare />{event.hasFeedbackForm ? "Edit form" : "Set up a form"}
            </Button>
          </HStack>

          {!event.hasFeedbackForm ? (
            <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
              <Text fontSize="sm" color="fg.muted">No feedback form on this event.</Text>
            </Box>
          ) : (
            <Stack gap={4}>
              {link === null ? null : (
                <Stack gap={2}>
                  <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
                    The link to share
                  </Text>
                  <HStack gap={2} wrap="wrap">
                    <Code fontSize="xs" px={3} py={2} maxW="full" overflowX="auto">{link}</Code>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        void navigator.clipboard.writeText(link).then(
                          () => toastSuccess("Link copied"),
                          () => toastError("The link could not be copied"),
                        );
                      }}
                    >
                      <LuCopy />Copy
                    </Button>
                  </HStack>
                  <Text fontSize="xs" color="fg.muted">
                    {event.feedbackClosesOn === null
                      ? "No closing date — it stays open until you rotate the link."
                      : `Closes on ${event.feedbackClosesOn}.`}
                  </Text>
                </Stack>
              )}

              {responses === undefined ? (
                <ListSkeleton label="Loading responses" rows={2} />
              ) : (
                <Stack gap={3}>
                  <HStack gap={6} wrap="wrap">
                    <Stat label="Responses" value={String(responses.count)} />
                    <Stat label="Confirmed attendance" value={String(responses.attended)} />
                    <Stat
                      label="Rate"
                      value={responses.rate === null ? "—" : `${Math.round(responses.rate * 100)}%`}
                    />
                  </HStack>

                  {responses.count === 0 ? (
                    <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
                      <Text fontSize="sm" color="fg.muted">Nothing yet.</Text>
                    </Box>
                  ) : (
                    <Stack gap={2}>
                      {responses.responses.map((response, index) => (
                        <Box key={index} borderWidth="1px" borderColor="border" px={4} py={3}>
                          <Stack gap={1}>
                            {Object.entries(response.responseData).map(([key, value]) => (
                              <HStack key={key} gap={2} align="baseline">
                                <Text fontSize="xs" color="fg.muted" minW="8rem">{key}</Text>
                                <Text fontSize="sm">
                                  {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value ?? "—")}
                                </Text>
                              </HStack>
                            ))}
                            <Text mt={1} fontSize="xs" color="fg.muted">{response.submittedDay}</Text>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Card.Body>

      <FeedbackFormDialog event={event} open={editing} onClose={() => setEditing(false)} />
    </Card.Root>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
        {label}
      </Text>
      <Text fontSize="2xl">{value}</Text>
    </Box>
  );
}

/**
 * A deliberately small form builder: a closing date, a rotate switch, and a
 * starting set of questions.
 *
 * Not the full field editor. A feedback form is four or five questions and the
 * full editor is built around a task's answers belonging to a person, which is
 * the one thing this form must never do. When somebody wants more than this,
 * the right move is to share the editor properly rather than to grow a second
 * one here.
 */
function FeedbackFormDialog({
  event,
  open,
  onClose,
}: {
  event: EventDoc;
  open: boolean;
  onClose: () => void;
}) {
  const setForm = useMutation(api.events.setEventFeedbackForm);
  const [closesOn, setClosesOn] = useState(event.feedbackClosesOn ?? "");
  const [saving, setSaving] = useState(false);

  async function apply(schema: unknown, rotateToken: boolean) {
    setSaving(true);
    try {
      const result = await setForm({
        eventId: event._id,
        formSchema: schema as never,
        closesOn: closesOn === "" ? null : closesOn,
        rotateToken,
      });
      toastSuccess(
        schema === null ? "Feedback form removed" : "Feedback form saved",
        result.token === null ? undefined : "The link is on the event page.",
      );
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The form could not be saved");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  const standardForm = {
    fields: [
      { key: "useful", label: "Was it useful?", type: "boolean", required: true },
      { key: "rating", label: "Out of ten", type: "number", required: true, min: 1, max: 10 },
      { key: "notes", label: "Anything else", type: "textarea", required: false },
    ],
  };

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Feedback form</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  Three questions — useful, a score out of ten, and anything else. Answers are
                  anonymous.
                </Text>
                <Field.Root>
                  <Field.Label>Closes on</Field.Label>
                  <Input type="date" value={closesOn} onChange={(e) => setClosesOn(e.target.value)} />
                  <Field.HelperText>
                    The ordinary way a form closes. Leave it blank to keep it open.
                  </Field.HelperText>
                </Field.Root>

                {!event.hasFeedbackForm ? null : (
                  <Box borderWidth="1px" borderColor="border" px={4} py={3}>
                    <Text fontSize="sm" fontWeight="medium">Rotating the link</Text>
                    <Text mt={1} fontSize="sm" color="fg.muted">
                      Closes the form immediately and permanently for anybody holding the old link.
                      For a link that has been shared where it should not have been — not for
                      closing a form, which the date above does.
                    </Text>
                    <Button
                      mt={3}
                      size="xs"
                      variant="outline"
                      colorPalette="orange"
                      loading={saving}
                      onClick={() => void apply(standardForm, true)}
                    >
                      <LuRefreshCw />Rotate the link
                    </Button>
                  </Box>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              {!event.hasFeedbackForm ? null : (
                <Button
                  variant="outline"
                  colorPalette="red"
                  mr="auto"
                  loading={saving}
                  onClick={() => void apply(null, false)}
                >
                  Remove the form
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button colorPalette="blue" loading={saving} onClick={() => void apply(standardForm, false)}>
                {event.hasFeedbackForm ? "Save" : "Add the form"}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
